import {
  CreateAnalyticsDto,
  CreateAnalyticsDtoAction,
  CreateAnalyticsDtoEntityType,
  CreateAnalyticsDtoType,
  CreateUserActionLogDto,
  CreateUserActionLogDtoActionType,
  CreateUserActionLogDtoEntityType,
  CreateUserActionLogDtoUserType,
  JobResponseEntity,
  LoginResponse,
} from "@/src/hooks/api/generated";

import {
  AutomaticApplySkipReason,
  IAutomaticApplySkip,
  truncateSkipDetail,
} from "./automaticApplySkipReason";

export interface IJobViewEvents {
  analytics: CreateAnalyticsDto;
  /** Absent when there is no member or non-member identity to log against. */
  userActionLog?: CreateUserActionLogDto;
}

/**
 * The skip reason is recorded only where a hand-off was possible and did not
 * happen: NULL when there is no utm_source and NULL when the hand-off ran.
 * Every other view gets a code, falling back to `unknown` rather than nothing,
 * so an unclassified path shows up in queries instead of hiding as NULL.
 */
function resolveSkip(
  utmSource: string | null,
  isAutomatedVisit: boolean,
  skip: IAutomaticApplySkip | undefined
): Pick<
  CreateAnalyticsDto,
  "auto_redirect_skip_reason" | "auto_redirect_skip_detail"
> {
  if (utmSource === null || isAutomatedVisit) {
    return {};
  }
  return {
    auto_redirect_skip_reason: skip?.reason ?? AutomaticApplySkipReason.UNKNOWN,
    auto_redirect_skip_detail: truncateSkipDetail(skip?.detail),
  };
}

/**
 * Builds the job view's analytics and Brevo payloads.
 *
 * A view is sent in one of two ways - through the generated client for ordinary
 * visits, or as a beacon during the automatic hand-off so it survives the
 * navigation away. Both go through here so the two rows cannot drift apart.
 */
export function buildJobViewEvents({
  jobDetails,
  authData,
  isAuthenticated,
  uuid,
  nonMemberUuid,
  searchParams,
  isAutomatedVisit,
  handoffSkip,
}: {
  jobDetails: JobResponseEntity;
  authData?: LoginResponse;
  isAuthenticated?: boolean;
  uuid?: string;
  nonMemberUuid?: string;
  searchParams: { get(name: string): string | null };
  isAutomatedVisit: boolean;
  /** Why this visit was not handed off; see resolveSkip. */
  handoffSkip?: IAutomaticApplySkip;
}): IJobViewEvents {
  const utmSource = searchParams.get("utm_source");
  const analytics: CreateAnalyticsDto = {
    action: CreateAnalyticsDtoAction.views,
    entity_id: jobDetails.id,
    entity_type: CreateAnalyticsDtoEntityType.jobs,
    type: CreateAnalyticsDtoType.job_listing,
    userId: authData?.id ? Number(authData?.id) : undefined,
    uuid: authData ? undefined : uuid,
    utm_source: utmSource ?? undefined,
    utm_medium: searchParams.get("utm_medium") ?? undefined,
    utm_campaign: searchParams.get("utm_campaign") ?? undefined,
    utm_term: searchParams.get("utm_term") ?? undefined,
    utm_content: searchParams.get("utm_content") ?? undefined,
    auto_redirect: isAutomatedVisit,
    ...resolveSkip(utmSource, isAutomatedVisit, handoffSkip),
  };
  if (!isAuthenticated && !nonMemberUuid) {
    return { analytics };
  }
  return {
    analytics,
    userActionLog: {
      action_type: CreateUserActionLogDtoActionType.JOB_VIEWED,
      entity_type: CreateUserActionLogDtoEntityType.job,
      entity_id: jobDetails.id,
      uuid: (authData ? authData.uuid : nonMemberUuid) ?? "",
      user_type: authData
        ? CreateUserActionLogDtoUserType.member
        : CreateUserActionLogDtoUserType["non-member"],
      auto_redirect: isAutomatedVisit,
    },
  };
}
